import { useEffect, useMemo } from 'react';
import useClient from 'hooks/useClient';
import useFirebase from 'hooks/useFirebase';

export type Variant = 'variantA' | 'variantB';

interface UseTestABProps {
  testName?: string;
  customPercentVariantA?: number;
}

interface UseTestABReturn {
  variant: Variant;
}

// Hook for test AB
export const useTestAB = ({
  testName = 'default',
  customPercentVariantA = 50,
}: UseTestABProps): UseTestABReturn => {
  const { firebaseTestAB } = useFirebase();
  const { client } = useClient();

  const percentageVariantAFromFirebase =
    firebaseTestAB[testName as keyof typeof firebaseTestAB];

  const percentVarianAToChecked =
    percentageVariantAFromFirebase !== undefined
      ? percentageVariantAFromFirebase
      : customPercentVariantA;

  const clientMobilePhone: number | undefined = client?.mobilePhone?.mobilePhone
    ? Number(client?.mobilePhone?.mobilePhone)
    : undefined;

  // Compute variant synchronously: restores from sessionStorage for session consistency,
  // or computes fresh from current Firebase percentage (picked up on new sessions).
  const variant = useMemo<Variant>(() => {
    if (!clientMobilePhone) return 'variantA';

    const sessionName = `ab_test_${testName}_${clientMobilePhone}`;
    const stored = sessionStorage.getItem(sessionName) as Variant | null;

    if (stored) return stored;

    return getVariantFromClientMobilePhone(
      clientMobilePhone,
      percentVarianAToChecked,
    );
  }, [clientMobilePhone, testName, percentVarianAToChecked]);

  // Persist the variant for the rest of the session
  useEffect(() => {
    if (!clientMobilePhone) return;
    const sessionName = `ab_test_${testName}_${clientMobilePhone}`;
    sessionStorage.setItem(sessionName, variant);
  }, [clientMobilePhone, testName, variant]);

  return { variant };
};

// Returns variantA for 30% of users and variantB for 70% of users
const getVariantFromClientMobilePhone = (
  clientMobilePhone: number,
  percentageVariantA: number,
): Variant => {
  // From value to percentage (0-99)
  const percentage = Math.abs(clientMobilePhone) % 100;

  // Return variantA/variantB
  return percentage < percentageVariantA ? 'variantA' : 'variantB';
};

export default useTestAB;
